import { identity } from 'lodash';
import { createDeepSelector } from '../../../../../utils/milanoteReselect/milanoteReselect';
import type { CellTypeObjectWithAuto, CellSelections } from '../../../../../../common/table/TableTypes';
import { ActiveTableSelector } from '../../../bridge/store/hotTableStoreHooks';
import { ActiveTableObject } from '../../../bridge/store/hotTableStoreTypes';
import { selectActiveTableCellSelection } from '../../../bridge/store/selectors/activeHotTableCellSelectionSelectors';
import { getHotTableCellBackgroundColor } from '../../../bridge/utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import { getSharedCellTypeFormatOptionsFromHot } from '../../../bridge/utils/hotInstanceUtils/cell/hotTableCellTypeUtils';
import {
    getHotTableCommonTextAlignment,
    getHotTableCommonVerticalAlignment,
    hasHotTableCellSelection,
} from '../../utils/hotInstanceUtils/cell/hotTableAlignmentUtils';
import { getAllCellsBetween } from '../../../bridge/utils/selectionUtils/tableSelectionGeometryUtils';
import { getHotTableCommonTextStyle } from '../../../bridge/utils/dataUtils/cell/tableCellStyleUtils';

/**
 * Get the background colour of the current cell selection.
 */
export const selectActiveTableCurrentCellBackgroundColor: ActiveTableSelector<string | null | undefined> = (
    activeTable,
) => {
    if (!activeTable?.instance) return null;
    const currentSelection = selectActiveTableCellSelection(activeTable);
    return getHotTableCellBackgroundColor(activeTable.instance, currentSelection);
};

/**
 * Returns the shared cell type formatting values for the active selection using live HoT cell metadata.
 */
export const selectActiveTableSharedCellTypeFormatOptions: ActiveTableSelector<CellTypeObjectWithAuto | undefined> =
    createDeepSelector<
        ActiveTableObject | undefined,
        CellSelections | undefined,
        ActiveTableObject | undefined,
        CellTypeObjectWithAuto | undefined
    >(selectActiveTableCellSelection, identity, (currentSelection, activeTableState) => {
        if (!activeTableState || !currentSelection?.length) return;

        return getSharedCellTypeFormatOptionsFromHot(activeTableState.instance, getAllCellsBetween(currentSelection));
    });

/**
 * Returns the common text alignment shared by the active table cell selection.
 */
export const selectActiveTableCommonTextAlignment: ActiveTableSelector<string | undefined, { locale: string }> = (
    activeTable,
    { locale },
) => {
    if (!activeTable?.instance) return;

    const currentSelection = selectActiveTableCellSelection(activeTable);
    return getHotTableCommonTextAlignment(activeTable.instance, locale, currentSelection);
};

/**
 * Returns the common vertical alignment shared by the active table cell selection.
 */
export const selectActiveTableCommonVerticalAlignment: ActiveTableSelector<string | undefined> = (activeTable) => {
    if (!activeTable?.instance) return;

    const currentSelection = selectActiveTableCellSelection(activeTable);
    return getHotTableCommonVerticalAlignment(activeTable.instance, currentSelection);
};

/**
 * Returns the text styles shared by every cell in the active table selection.
 */
export const selectActiveTableCommonTextStyle: ActiveTableSelector<string[] | undefined> = (activeTable) => {
    if (!activeTable?.instance) return;

    const currentSelection = selectActiveTableCellSelection(activeTable);
    return getHotTableCommonTextStyle(activeTable.instance, currentSelection);
};

/**
 * Returns whether the active table alignment tool should be disabled.
 */
export const selectIsActiveTableAlignmentToolDisabled: ActiveTableSelector<boolean> = (activeTable) => {
    const currentSelection = selectActiveTableCellSelection(activeTable);
    return !hasHotTableCellSelection(currentSelection);
};
